<template>
  <div class="w-full">
    <!-- Label -->
    <div class="flex items-center gap-2 font-semibold">
      <span>File</span>

      <!-- Info Icon with Tooltip -->
      <div class="relative group flex items-center">
        <Icon
          icon="mdi:information-outline"
          class="text-gray-400 hover:text-mocha-mousse cursor-pointer text-lg transition"
        />
        <div
          class="absolute hidden group-hover:block left-1/2 -translate-x-1/2 top-6 z-20 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 whitespace-normal w-72 shadow-lg"
        >
          Drag and drop or select a zip file, a standard file, or a folder to
          upload it
        </div>
      </div>
    </div>

    <!-- Options + Dropzone -->
    <template v-if="!hasSelection">
      <div class="mt-2 flex h-8 rounded-lg w-full overflow-hidden">
        <label
          :class="[
            'w-1/2 h-full flex justify-center items-center cursor-pointer transition-colors duration-200 border rounded-l-lg select-none',
            selectedOption === 'zip'
              ? 'bg-spicy-mustard-300 border-mocha-mousse text-mocha-mousse-700'
              : 'text-gray-700 border-gray-300 hover:bg-spicy-mustard-200 hover:border-spicy-mustard',
          ]"
        >
          <input
            type="radio"
            name="file-type"
            value="zip"
            class="hidden"
            @click="onLabelSelected('zip')"
          />
          <Icon
            icon="mdi:zip-box-outline"
            class="mr-1 text-mocha-mousse text-lg"
          />
          <span>Zip / Single File</span>
        </label>

        <label
          :class="[
            'w-1/2 h-full flex justify-center items-center cursor-pointer transition-colors duration-200 border rounded-r-lg select-none',
            selectedOption === 'folder'
              ? 'bg-spicy-mustard-300 border-mocha-mousse text-mocha-mousse-700'
              : 'text-gray-700 border-gray-300 hover:bg-spicy-mustard-200 hover:border-spicy-mustard',
          ]"
        >
          <input
            type="radio"
            name="file-type"
            value="folder"
            class="hidden"
            @click="onLabelSelected('folder')"
          />
          <Icon
            icon="mdi:folder-outline"
            class="mr-1 text-mocha-mousse-600 text-lg"
          />
          <span>Folder</span>
        </label>
      </div>

      <!-- Dropzone -->
      <div
        id="file"
        class="mt-4 h-40 flex flex-col justify-center items-center w-full border border-gray-300 rounded-xl p-4 text-center text-gray-500 bg-gray-50 transition-all cursor-pointer"
        :class="{
          'border-blue-500 bg-blue-100 text-blue-700': isDragging,
          'border-red-500 bg-red-50 text-red-600': showError,
        }"
        @dragover.prevent="isDragging = true"
        @dragleave="isDragging = false"
        @drop="handleDrop"
        @click="openFilePicker"
      >
        <Icon
          :icon="
            selectedOption === 'zip'
              ? 'mdi:file-upload-outline'
              : 'mdi:folder-upload-outline'
          "
          class="text-4xl mb-2 text-mocha-mousse-500"
        />
        <p>Click here or</p>
        <p v-if="selectedOption === 'zip'">Drag & drop file</p>
        <p v-else>Drag & drop folder</p>

        <p v-if="showError" class="text-red-500 text-sm mt-2">
          {{
            selectedOption === "zip"
              ? "Files are mandatory"
              : "Folder is mandatory"
          }}
        </p>
      </div>
    </template>

    <!-- Selection preview -->
    <div v-else class="mt-4 w-full">
      <div
        id="selected-file"
        class="p-3 flex items-center justify-between border border-gray-300 rounded-xl bg-white/80 backdrop-blur-md shadow-sm"
      >
        <!-- Left: Icon -->
        <div class="flex items-center gap-2">
          <Icon
            v-if="extension_icon"
            :icon="extension_icon"
            class="text-mocha-mousse text-2xl"
          />
          <Icon v-else icon="mdi:file-outline" class="text-gray-400 text-2xl" />
        </div>

        <!-- Center: File name or loader -->
        <div class="flex-1 text-center">
          <Icon
            v-if="loadingData"
            icon="eos-icons:loading"
            class="text-mocha-mousse text-2xl animate-spin"
          />
          <p v-else class="font-medium truncate">
            {{ formState.filename }}
          </p>
        </div>

        <!-- Right: Remove icon -->
        <div class="relative group">
          <Icon
            icon="mdi:close-circle-outline"
            class="text-gray-400 hover:text-red-500 cursor-pointer text-xl transition"
            @click="removeFile"
          />
          <div
            class="absolute hidden group-hover:block right-0 top-6 bg-gray-800 text-white text-xs rounded-md px-2 py-1 shadow-lg"
          >
            Remove file
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>

<script setup>
import { ref, computed } from "vue";
import { Icon } from "@iconify/vue";
import { useFormStore } from "../stores/FormStore";
import * as zip from "@zip.js/zip.js";
import { useLogger } from "@/stores/useLogger";
import { useLoadingService } from "@/services/LoadingService";

const loadingSvc = useLoadingService();
const logger = useLogger();
logger.wireLifecycle();

const formStore = useFormStore();
const formState = formStore.formState;

// UI state
const loadingData = ref(false);
const selectedOption = ref("zip");
const isDragging = ref(false);
const formChecked = ref(false);
const extension = ref("");

// derived — use store's derived where possible
const hasSelection = computed(() => formStore.hasFile); // ← from store
const showError = computed(() => formChecked.value && !hasSelection.value);

// icons mapping
const extension_icon = ref("mdi:file-outline");

// helpers
const getExt = (name = "") => {
  const i = name.lastIndexOf(".");
  return i > 0 ? name.slice(i + 1).toLowerCase() : "";
};
const uniq = (arr) => [...new Set(arr)];
const bytesToKiB = (n) => Math.round(n / 1024);

function updateExtIcon() {
  const ext =
    extension.value || (formState.file ? inferExtFromCustomType() : "file");
  const map = {
    folder: "mdi:folder-outline",
    zip: "mdi:zip-box-outline",
    pdf: "mdi:file-pdf-box",
    txt: "mdi:file-document-outline",
    csv: "mdi:file-delimited-outline",
    json: "mdi:file-code-outline",
  };
  extension_icon.value = map[ext] || "mdi:file-outline";
}

function inferExtFromCustomType() {
  const t = formState.file?.custom_type;
  if (t === "folder") return "folder";
  if (t === "zip") return "zip";
  return getExt(formState.filename || "") || "file";
}

// normalize selection
async function ingestFiles({ files, isFolderDrop }) {
  if (!files || files.length === 0) return;

  const taskId = `upload-${Date.now()}-${Math.floor(Math.random() * 100000)}`; // unique ID per upload
  loadingSvc.start(taskId, {
    title: "Uploading files...",
  });

  loadingData.value = true;

  try {
    const arr = Array.from(files).filter(Boolean);

    const totalBytes = arr.reduce((s, f) => s + (f.size || 0), 0);
    const exts = uniq(arr.map((f) => getExt(f.name)));
    const largest = arr.reduce((a, b) => (!a || b.size > a.size ? b : a), null);

    const baseMeta = {
      source: isFolderDrop
        ? "drop-folder"
        : selectedOption.value === "folder"
        ? "picker-folder"
        : "single",
      count: arr.length,
      total_kib: bytesToKiB(totalBytes),
      unique_exts: exts.slice(0, 10),
      largest_name: largest?.name,
      largest_kib: largest ? bytesToKiB(largest.size) : undefined,
    };

    const treatAsFolder =
      isFolderDrop ||
      selectedOption.value === "folder" ||
      (arr.length > 1 && arr[0].webkitRelativePath);

    if (treatAsFolder) {
      const topName =
        (arr[0].webkitRelativePath &&
          arr[0].webkitRelativePath.split("/")[0]) ||
        arr[0].name.split(".")[0] ||
        "folder";
      const zipped = await zipFiles(arr, topName);
      setSelection(zipped, `${topName}.zip`, "folder");

      logger.log("upload_drop", {
        metadata: {
          ...baseMeta,
          folder: topName,
          zipped_kib: bytesToKiB(zipped.size),
        },
      });
    } else {
      const f = arr[0];
      const isZip =
        ["application/zip", "application/x-zip-compressed"].includes(f.type) ||
        getExt(f.name) === "zip";

      setSelection(f, f.name, isZip ? "zip" : "single-file");

      logger.log("upload_drop", {
        metadata: {
          ...baseMeta,
          name: f.name,
          size: f.size,
          type: f.type,
          is_zip: isZip,
        },
      });
    }
  } finally {
    loadingData.value = false;
    loadingSvc.stop(taskId); // stop the loader
  }
}

function setSelection(file, filename, customType) {
  // Use store API
  formStore.setFile(file, filename);
  // Attach custom_type to the file object (used by inferExtFromCustomType)
  if (formState.file) {
    formState.file.custom_type = customType;
  }
  extension.value =
    getExt(filename) || (customType === "folder" ? "folder" : "");
  updateExtIcon();
}

async function handleDrop(e) {
  e.preventDefault();
  isDragging.value = false;
  const items = e.dataTransfer?.items ? [...e.dataTransfer.items] : [];
  const supportsFSAccess =
    "getAsFileSystemHandle" in DataTransferItem.prototype;
  let files = [];

  if (supportsFSAccess) {
    const handles = await Promise.all(
      items
        .filter((i) => i.kind === "file")
        .map((i) => i.getAsFileSystemHandle())
    );
    let isFolder = false;
    for (const h of handles) {
      if (h.kind === "directory") {
        isFolder = true;
        await readDirectoryHandle(h, files, `${h.name}/`);
      } else if (h.kind === "file") {
        const f = await h.getFile();
        files.push(f);
      }
    }
    await ingestFiles({ files, isFolderDrop: isFolder });
  } else {
    const entries = items
      .filter((i) => i.kind === "file")
      .map((i) => i.webkitGetAsEntry?.())
      .filter(Boolean);
    let isFolder = false;
    for (const entry of entries) {
      if (entry.isDirectory) {
        isFolder = true;
        await readWebkitEntry(entry, files, `${entry.name}/`);
      } else if (entry.isFile) {
        const f = await new Promise((r) => entry.file(r));
        files.push(f);
      }
    }
    await ingestFiles({ files, isFolderDrop: isFolder });
  }
}

async function readDirectoryHandle(dirHandle, out, root) {
  if (!dirHandle?.entries) return;
  for await (const [, handle] of dirHandle.entries()) {
    if (handle.kind === "directory") {
      await readDirectoryHandle(handle, out, `${root}${handle.name}/`);
    } else if (handle.kind === "file") {
      const f = await handle.getFile();
      Object.defineProperty(f, "webkitRelativePath", {
        value: `${root}${handle.name}`,
      });
      out.push(f);
    }
  }
}
async function readWebkitEntry(entry, out, root) {
  if (entry.isDirectory) {
    const reader = entry.createReader();
    let entries = [];
    do {
      entries = await new Promise((resolve) => reader.readEntries(resolve));
      for (const e of entries)
        await readWebkitEntry(e, out, `${root}${e.name}/`);
    } while (entries.length > 0);
  } else if (entry.isFile) {
    const f = await new Promise((r) => entry.file(r));
    Object.defineProperty(f, "webkitRelativePath", {
      value: root.replace(/\/$/, ""),
    });
    out.push(f);
  }
}

function openFilePicker() {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "*";
  if (selectedOption.value === "folder") {
    input.multiple = true;
    input.webkitdirectory = true;
  }
  input.onchange = () =>
    ingestFiles({
      files: input.files,
      isFolderDrop: selectedOption.value === "folder",
    });
  input.click();
}

async function zipFiles(files, folderName) {
  const zipWriter = new zip.ZipWriter(new zip.BlobWriter("application/zip"), {
    bufferedWrite: true,
  });
  for (const file of files) {
    const path = file.webkitRelativePath || file.name;
    await zipWriter.add(path, new zip.BlobReader(file));
  }
  const blob = await zipWriter.close();
  return new File([blob], `${folderName}.zip`, { type: "application/zip" });
}

function removeFile() {
  formStore.clearFile(); // ← store API
  extension.value = "";
  updateExtIcon();
}

function validateInput() {
  formChecked.value = true;
  return formStore.hasFile; // ← store derived
}
function onLabelSelected(val) {
  selectedOption.value = val;
  logger.log("upload_file_type", { metadata: { newValue: val } });
}

defineExpose({ validateInput, loadingData });
</script>
